import { LiveKitRoom, RoomAudioRenderer } from "@livekit/components-react"
import "@livekit/components-styles"
import * as Sentry from "@sentry/react"
import { MediaDeviceFailure, Participant } from "livekit-client"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import CallLoading from "../../components/CustomVideoConference/CallLoading"
import { AnalyticsEvents } from "../../types/Analytics"
import { CallDisconnectType } from "../../types/Call"
import { isInIframe, postMessageToParent } from "../../utils/func"
import {
  getStoredCallId,
  isDev,
  isLocalDev,
  isStaging,
  LIVEKIT_ROOM_STORAGE_KEY,
  type LivekitRoomInfo,
} from "../../utils/general"
import { useAppContext, useAppDispatchContext } from "../Context"
import { useMixpanel } from "../hooks/useMixpanel"
import { MyVideoConference } from "./MyVideoConference"

const IDLE_TIMEOUT = 2 * 60 // 2 minutes
const BUFFER_TIME = 2 // 2 seconds

// When running multiple video generation models in parallel, we need to connect to different LiveKit servers
// However, let's leave this as a backup in case the token does not contain the server URL as metadata
const defaultServerUrl =
  import.meta.env.VITE_LIVEKIT_URL ??
  (isLocalDev
    ? "ws://localhost:7880"
    : isDev
      ? "wss://dev-38rwe5wn.livekit.cloud"
      : isStaging
        ? "wss://staging-56walglr.livekit.cloud"
        : "wss://prod-w0h88kyi.livekit.cloud")

export default function LiveRoom() {
  const context = useAppContext()
  const dispatch = useAppDispatchContext()
  const navigate = useNavigate()
  const { id: agentId } = useParams()
  const isEmbed = isInIframe()
  const callId = getStoredCallId()

  const [serverUrl, setServerUrl] = useState(defaultServerUrl)
  const [token, setToken] = useState("")
  const [isConnected, setIsConnected] = useState(false)
  const [participants, setParticipants] = useState<Participant[]>([])
  const prevParticipantsRef = useRef(participants)
  const [lastMessageTime, setLastMessageTime] = useState(Date.now())
  const [duration, setDuration] = useState(0)
  const [maxCallDuration, setMaxCallDuration] = useState(3 * 60)
  const disconnectReason = CallDisconnectType.SessionExpired
  const [sessionStartTime] = useState(new Date(Date.now() + BUFFER_TIME * 1000))
  const [speakingParticipants, setSpeakingParticipants] = useState<string[]>([])
  const hasDisconnected = useRef(false)

  const { logAnalyticsEvent } = useMixpanel()

  const { agent } = context

  const roomOptions = useMemo(
    () => ({
      audioCaptureDefaults: context.audioDevice
        ? {
            deviceId: context.audioDevice,
          }
        : undefined,
      videoCaptureDefaults: context.videoDevice
        ? {
            deviceId: context.videoDevice,
          }
        : undefined,
    }),
    [],
  )

  useEffect(() => {
    const stored = sessionStorage.getItem(LIVEKIT_ROOM_STORAGE_KEY)
    if (!stored) {
      navigate(`/${agentId || ""}`, { replace: true })
      return
    }

    try {
      const roomInfo: LivekitRoomInfo = JSON.parse(stored)
      setServerUrl(roomInfo.url)
      setToken(roomInfo.token)
    } catch (e) {
      console.error("Failed to parse stored LiveKit room info:", e)
      navigate(`/${agentId || ""}`, { replace: true })
    }
  }, [agentId])

  const onDisconnected = useCallback(
    async (callDisconnectType: CallDisconnectType | null = null) => {
      if (hasDisconnected.current) {
        return
      }
      hasDisconnected.current = true
      sessionStorage.removeItem(LIVEKIT_ROOM_STORAGE_KEY)

      try {
        const duration = Math.floor(
          (new Date().getTime() - sessionStartTime.getTime()) / 1000,
        )

        logAnalyticsEvent(AnalyticsEvents.CallEnded, {
          duration,
          callDisconnectReason: callDisconnectType,
        })

        postMessageToParent({
          type: "CALL_ENDED",
          callId,
          duration,
          reason: callDisconnectType,
        })

        navigate("/rooms/" + agentId + "/post", { state: callDisconnectType })
      } catch (error) {
        console.error("Error during disconnect:", error)
        navigate("/rooms/" + agentId + "/post", { state: callDisconnectType })
      }
    },
    [dispatch, agentId, callId, navigate, sessionStartTime],
  )

  useEffect(() => {
    if (agent?.custom_branding) {
      return
    }

    // timer to check if the user is idle
    const idleCheck = setInterval(() => {
      const idle = Date.now() - lastMessageTime
      // Only disconnect if there are participants but none are speaking
      if (
        idle >= IDLE_TIMEOUT * 1000 &&
        participants.length > 0 &&
        speakingParticipants.length === 0
      ) {
        logAnalyticsEvent(AnalyticsEvents.IdleTimeout)
        onDisconnected(CallDisconnectType.InActive)
      }
    }, 1000)

    return () => clearInterval(idleCheck)
  }, [
    lastMessageTime,
    onDisconnected,
    participants,
    speakingParticipants,
    agent?.custom_branding,
  ])

  useEffect(() => {
    // timer to check if the call should end
    if (maxCallDuration <= 0) return

    if (duration >= maxCallDuration) {
      onDisconnected(disconnectReason)
    }
  }, [duration, maxCallDuration, disconnectReason, onDisconnected])

  useEffect(() => {
    let timer: NodeJS.Timeout

    timer = setInterval(() => {
      const currentSessionDuration = Math.floor(
        (new Date().getTime() - sessionStartTime.getTime()) / 1000,
      )
      setDuration(currentSessionDuration)
    }, 1000)

    return () => {
      if (timer) clearInterval(timer)
    }
  }, [sessionStartTime])

  useEffect(() => {
    if (!agent) return

    const maxMinutes = agent.max_session_length_minutes

    if (maxMinutes === null || maxMinutes === undefined) {
      setMaxCallDuration(0)
      return
    }

    if (maxMinutes <= 0) {
      onDisconnected(CallDisconnectType.CreditsExhausted)
      return
    }

    setMaxCallDuration(Math.floor(maxMinutes * 60))
  }, [agent])

  useEffect(() => {
    // check if any participant (User, Agent, Avatar) left the room
    const leftParticipants = prevParticipantsRef.current.filter(
      (previous) =>
        !participants.some((current) => current.identity === previous.identity),
    )
    // The agent worker sets this just before it leaves on purpose, so any
    // other leave, like a crashed worker, still counts as a failure. Keep the
    // key and values in sync with `END_REASON_ATTRIBUTE` in the worker's core.py
    const endReason = leftParticipants
      .map((participant) => participant.attributes?.["bey.end_reason"])
      .find((reason) => reason !== undefined)
    switch (endReason) {
      case "agent_ended":
        onDisconnected(CallDisconnectType.AgentEnded)
        break
      case "session_expired":
        onDisconnected(CallDisconnectType.SessionExpired)
        break
      case "idle":
        onDisconnected(CallDisconnectType.InActive)
        break
      default:
        if (leftParticipants.length > 0) {
          onDisconnected(CallDisconnectType.InvalidConfiguration)
        }
    }
    prevParticipantsRef.current = participants
  }, [participants])

  const handleUserMessage = useCallback(() => {
    setLastMessageTime(Date.now())
  }, [])

  const handleParticipantSpeaking = useCallback(
    (speakingParticipants: string[]) => {
      if (speakingParticipants.length > 0) {
        setLastMessageTime(Date.now())
      }
      setSpeakingParticipants(speakingParticipants)
    },
    [],
  )

  const handleMediaDeviceFailure = useCallback(
    (failure: MediaDeviceFailure) => {
      Sentry.captureMessage("Media Device Failure", {
        level: "error",
        tags: {
          "media.device.failure": failure.toString(),
        },
        extra: {
          mediaDeviceFailure: failure,
        },
      })
    },
    [],
  )

  const handleConnected = useCallback(() => {
    setIsConnected(true)
    const mediaStates = {
      audioEnabled: context.audioEnabled,
      videoEnabled: context.videoEnabled,
      connectionTime: new Date().toISOString(),
    }

    if (isDev && !isLocalDev) {
      console.log("LiveKit Room Connected - Media States:", mediaStates)

      Sentry.captureEvent({
        message: "LiveKit Room Media States",
        level: "info",
        tags: {
          "media.connection": "success",
          "media.audio.enabled": String(context.audioEnabled),
          "media.video.enabled": String(context.videoEnabled),
        },
        extra: {
          mediaStates,
        },
      })

      Sentry.addBreadcrumb({
        category: "media",
        message: "Media states after LiveKit room connection",
        level: "info",
        data: mediaStates,
      })

      Sentry.setContext("media_devices", mediaStates)
    }

    logAnalyticsEvent(AnalyticsEvents.MeetingJoined, {
      call_id: callId,
    })

    postMessageToParent({
      type: "CALL_STARTED",
      callId,
    })
  }, [context.audioEnabled, context.videoEnabled, callId])

  useEffect(() => {
    if (!isDev || isConnected) return

    const timeout = setTimeout(() => {
      console.warn("[DEV] Agent connection timeout — still loading after 30s")
      console.table(
        participants.map((p) => ({
          identity: p.identity,
          name: p.name,
          isLocal: p.isLocal,
          isAgent: p.isAgent,
          connectionQuality: p.connectionQuality,
          isSpeaking: p.isSpeaking,
          joinedAt: p.joinedAt?.toISOString(),
          tracks: Array.from(p.trackPublications.values()).map(
            (t) =>
              `${t.kind}:${t.trackName}:${t.isSubscribed ? "sub" : "unsub"}`,
          ),
        })),
      )
      console.log("[DEV] Room state:", {
        serverUrl,
        callId,
        agentId,
        participantCount: participants.length,
        token: token ? `${token.slice(0, 20)}...` : "empty",
      })
    }, 30_000)

    return () => clearTimeout(timeout)
  }, [isConnected, participants, serverUrl, callId, agentId, token])

  useEffect(() => {
    const handlePageHide = () => {
      sessionStorage.removeItem(LIVEKIT_ROOM_STORAGE_KEY)
    }

    window.addEventListener("pagehide", handlePageHide)
    return () => window.removeEventListener("pagehide", handlePageHide)
  }, [])

  return (
    <LiveKitRoom
      token={token}
      serverUrl={serverUrl}
      data-lk-theme="default"
      audio={context.audioEnabled}
      video={context.videoEnabled}
      style={{ height: "100dvh" }}
      onDisconnected={() => onDisconnected()}
      onConnected={handleConnected}
      options={roomOptions}
      onMediaDeviceFailure={handleMediaDeviceFailure}
    >
      <div data-testid="room-audio-renderer" style={{ display: "contents" }}>
        <RoomAudioRenderer />
      </div>
      {!isConnected && <CallLoading agent={agent} isEmbed={isEmbed} />}
      {isConnected && (
        <MyVideoConference
          setParticipants={setParticipants}
          isEmbed={isEmbed}
          maxDuration={maxCallDuration}
          onUserMessage={handleUserMessage}
          onDisconnected={onDisconnected}
          onParticipantSpeaking={handleParticipantSpeaking}
          duration={duration}
        />
      )}
    </LiveKitRoom>
  )
}
